// API helpers for saved payment methods (card management + 1-click purchases).
// Mirrors the pattern in coinShopApi.js – Bearer token auth, JSON fetch.

import { getValidAccessToken } from './getValidAccessToken';
import { createRequestId } from './requestId';

const jsonFetch = async (url, { method = 'GET', headers = {}, body } = {}) => {
  const res = await fetch(url, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const err = new Error(data?.error || text || 'Request failed');
    err.status = res.status;
    err.code = data?.code;
    throw err;
  }

  return data;
};

const requireToken = async () => {
  const accessToken = await getValidAccessToken();
  if (!accessToken) {
    const err = new Error('Not authenticated');
    err.code = 'UNAUTHORIZED';
    throw err;
  }
  return accessToken;
};

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

/** Fetch saved cards for the current user. Returns { paymentMethods: [...] } */
export const getPaymentMethods = async () => {
  const token = await requireToken();
  return await jsonFetch('/api/getPaymentMethods', {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
};

/** Redirect user to Stripe Checkout to save a card (mode: setup). */
export const createSetupSession = async () => {
  const token = await requireToken();
  return await jsonFetch('/api/createSetupSession', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: { requestId: createRequestId() },
  });
};

/** Detach a saved card. Returns { ok: true }. */
export const deletePaymentMethod = async (paymentMethodId) => {
  const token = await requireToken();
  return await jsonFetch('/api/deletePaymentMethod', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: { paymentMethodId },
  });
};

/**
 * Buy a coin pack using a saved card (1-click).
 * Returns { status: 'succeeded' }           → purchase complete
 *      or { status: 'requires_action', url } → redirect for 3DS
 */
export const buyPackWithSavedCard = async ({ packId, paymentMethodId }) => {
  const token = await requireToken();
  return await jsonFetch('/api/buyPackSavedCard', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: { packId, paymentMethodId, requestId: createRequestId() },
  });
};
